import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { chat as chatApi, projects } from "../../lib/api";
import { useUI } from "../../lib/store";
import type {
  ChatThreadSummary,
  TaskProposal,
  TrackProposal,
} from "../../lib/types";
import { Bubble, EmptyChat, StreamingBubble, ThinkingBubble, isCliUnavailable } from "./ChatBubble";
import { Composer } from "./Composer";
import { useChatThread } from "./useChatThread";
import {
  Check,
  ChevronLeft,
  Inbox,
  Pencil,
  Plus,
  Search,
  Trash,
} from "../shared/Icon";
import { cn } from "../../lib/cn";
import { relativeShort } from "../../lib/format";

/**
 * Right-pane chat: the managing-agent conversation, scoped to the selected
 * project. Data layer in ``useChatThread``. The "Threads" toggle swaps the
 * message stream for an inline browser (search, rename, all-projects).
 */
export function ChatPanel() {
  const projectId = useUI((s) => s.selectedProjectId);
  const activeId = useUI((s) => s.activeChatThreadId);
  const setActiveId = useUI((s) => s.setActiveChatThreadId);
  const setSelectedTaskId = useUI((s) => s.setSelectedTaskId);
  const setRightPaneMode = useUI((s) => s.setRightPaneMode);
  const chatModel = useUI((s) => s.chatModel);
  const setChatModel = useUI((s) => s.setChatModel);
  const qc = useQueryClient();

  const [browsing, setBrowsing] = useState(false);
  const [search, setSearch] = useState("");
  const [allProjects, setAllProjects] = useState(false);
  const trimmedSearch = search.trim();
  const scope = allProjects ? undefined : projectId ?? undefined;

  const { data: projectList } = useQuery({
    queryKey: ["projects"],
    queryFn: () => projects.list(),
  });
  const project = projectList?.find((p) => p.id === projectId);

  const { data: threads } = useQuery({
    queryKey: ["chat-threads", scope ?? "all", trimmedSearch],
    queryFn: () =>
      trimmedSearch
        ? chatApi.searchThreads(trimmedSearch, scope)
        : chatApi.listThreads(scope),
    enabled: browsing,
  });

  const refreshThreads = () =>
    qc.invalidateQueries({ queryKey: ["chat-threads"] });

  const { thread, messages, handleSend, pending, streamingText } = useChatThread({
    threadId: activeId,
    projectId,
    model: chatModel,
    onThreadsChanged: refreshThreads,
    onThreadCreated: (id) => setActiveId(id),
  });

  const deleteThread = useMutation({
    mutationFn: async (id: string) => chatApi.deleteThread(id),
    onSuccess: () => {
      setActiveId(null);
      refreshThreads();
    },
  });

  const renameThread = useMutation({
    mutationFn: async (args: { id: string; title: string }) =>
      chatApi.renameThread(args.id, args.title),
    onSuccess: () => refreshThreads(),
  });

  const acceptTask = useMutation({
    mutationFn: async (args: { proposal: TaskProposal; dispatch: boolean }) =>
      chatApi.acceptProposal(args.proposal, args.dispatch),
    onSuccess: (task) => {
      qc.invalidateQueries({ queryKey: ["kanban"] });
      setSelectedTaskId(task.id);
      setRightPaneMode("activity");
    },
  });

  const acceptTrack = useMutation({
    mutationFn: async (args: { proposal: TrackProposal; dispatch: boolean }) =>
      chatApi.acceptTrackProposal(args.proposal, args.dispatch),
    onSuccess: (track) => {
      qc.invalidateQueries({ queryKey: ["tracks"] });
      useUI.getState().setSelectedTrackId(track.id);
      useUI.getState().setMainView("tracks");
    },
  });

  const cliError = isCliUnavailable(messages);
  const showProjectBadge = allProjects || projectId == null;

  const scrollerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, pending, streamingText]);

  const openThread = (id: string) => {
    setActiveId(id);
    setBrowsing(false);
  };

  const newChat = () => {
    setActiveId(null);
    setBrowsing(false);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-bg-panel">
      <PanelHeader
        browsing={browsing}
        title={thread?.title ?? null}
        isFresh={activeId == null}
        connected={!cliError}
        onToggleBrowse={() => setBrowsing((v) => !v)}
        onNew={newChat}
        onClear={() => activeId && deleteThread.mutate(activeId)}
        canClear={Boolean(activeId)}
      />
      {browsing ? (
        <ThreadBrowser
          threads={threads ?? []}
          activeId={activeId}
          search={search}
          onSearch={setSearch}
          allProjects={allProjects}
          onToggleAllProjects={
            projectId == null ? undefined : () => setAllProjects((v) => !v)
          }
          showProjectBadge={showProjectBadge}
          onOpen={openThread}
          onRename={(id, title) => renameThread.mutate({ id, title })}
        />
      ) : (
        <>
          <div ref={scrollerRef} className="flex-1 overflow-y-auto">
            <div className="px-4 py-4 space-y-5">
              {messages.length === 0 && streamingText === null && !pending ? (
                <EmptyChat />
              ) : (
                messages.map((m, i) => (
                  <Bubble
                    key={i}
                    msg={m}
                    onAccept={(dispatch) =>
                      m.task_proposal &&
                      acceptTask.mutate({ proposal: m.task_proposal, dispatch })
                    }
                    onAcceptTrack={(dispatch) =>
                      m.track_proposal &&
                      acceptTrack.mutate({
                        proposal: m.track_proposal,
                        dispatch,
                      })
                    }
                  />
                ))
              )}
              {pending && streamingText !== null ? (
                <StreamingBubble text={streamingText} />
              ) : pending ? (
                <ThinkingBubble />
              ) : null}
            </div>
          </div>
          <Composer
            onSend={handleSend}
            pending={pending}
            attribution={project?.name}
            model={chatModel}
            onModelChange={setChatModel}
          />
        </>
      )}
    </div>
  );
}

function PanelHeader({
  browsing,
  title,
  isFresh,
  connected,
  onToggleBrowse,
  onNew,
  onClear,
  canClear,
}: {
  browsing: boolean;
  title: string | null;
  isFresh: boolean;
  connected: boolean;
  onToggleBrowse: () => void;
  onNew: () => void;
  onClear: () => void;
  canClear: boolean;
}) {
  return (
    <div
      className={cn(
        "sticky top-0 z-10 h-9 px-3 flex items-center gap-2",
        "bg-bg-panel border-b border-border-subtle",
      )}
    >
      {browsing ? (
        <>
          <IconBtn label="Back to conversation" onClick={onToggleBrowse}>
            <ChevronLeft />
          </IconBtn>
          <span className="text-small text-text-secondary truncate min-w-0 flex-1">
            Threads
          </span>
          <IconBtn label="New chat" onClick={onNew}>
            <Plus />
          </IconBtn>
        </>
      ) : (
        <>
          <span
            className={cn(
              "h-1.5 w-1.5 rounded-full shrink-0",
              connected ? "bg-status-success" : "bg-status-error",
            )}
            aria-hidden="true"
          />
          <span className="text-small text-text-secondary truncate min-w-0 flex-1">
            {isFresh ? "New chat" : title || "New chat"}
          </span>
          <IconBtn label="Browse threads" onClick={onToggleBrowse}>
            <Inbox />
          </IconBtn>
          <IconBtn label="New chat" onClick={onNew}>
            <Plus />
          </IconBtn>
          <IconBtn
            label="Clear chat"
            onClick={onClear}
            disabled={!canClear}
            danger
          >
            <Trash />
          </IconBtn>
        </>
      )}
    </div>
  );
}

function ThreadBrowser({
  threads,
  activeId,
  search,
  onSearch,
  allProjects,
  onToggleAllProjects,
  showProjectBadge,
  onOpen,
  onRename,
}: {
  threads: ChatThreadSummary[];
  activeId: string | null;
  search: string;
  onSearch: (q: string) => void;
  allProjects: boolean;
  onToggleAllProjects?: () => void;
  showProjectBadge: boolean;
  onOpen: (id: string) => void;
  onRename: (id: string, title: string) => void;
}) {
  const searching = search.trim().length > 0;
  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="px-3 pt-3 pb-2 space-y-2">
        <div className="relative">
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-text-quaternary pointer-events-none">
            <Search />
          </span>
          <input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search threads"
            className={cn(
              "w-full h-8 pl-7 pr-2 rounded-md bg-bg-inset text-small",
              "text-text-primary placeholder:text-text-quaternary",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
            )}
          />
        </div>
        {onToggleAllProjects && (
          <button
            onClick={onToggleAllProjects}
            title={
              allProjects
                ? "Showing threads from every project"
                : "Show threads from every project"
            }
            className={cn(
              "h-7 px-2 inline-flex items-center rounded-md text-caption",
              "transition-colors duration-fast ease-out",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
              allProjects
                ? "bg-bg-inset text-text-primary"
                : "text-text-tertiary hover:text-text-primary hover:bg-bg-inset",
            )}
          >
            All projects
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-1 pb-3 space-y-px">
        {threads.length === 0 && (
          <div className="px-3 py-6 text-center text-small text-text-tertiary">
            {searching ? "No threads match your search." : "No conversations yet."}
          </div>
        )}
        {threads.map((t) => (
          <ThreadRow
            key={t.id}
            thread={t}
            active={t.id === activeId}
            showProjectBadge={showProjectBadge}
            onOpen={() => onOpen(t.id)}
            onRename={(title) => onRename(t.id, title)}
          />
        ))}
      </div>
    </div>
  );
}

function ThreadRow({
  thread,
  active,
  showProjectBadge,
  onOpen,
  onRename,
}: {
  thread: ChatThreadSummary;
  active: boolean;
  showProjectBadge: boolean;
  onOpen: () => void;
  onRename: (title: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(thread.title || "");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const startEditing = () => {
    setDraft(thread.title || "");
    setEditing(true);
  };

  const commit = () => {
    const next = draft.trim();
    if (next && next !== thread.title) onRename(next);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="px-3 py-1.5 rounded-md bg-bg-inset flex items-center gap-1">
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") setEditing(false);
          }}
          onBlur={commit}
          className={cn(
            "flex-1 min-w-0 bg-transparent text-small text-text-primary",
            "focus-visible:outline-none",
          )}
        />
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={commit}
          aria-label="Save title"
          title="Save"
          className="h-6 w-6 inline-flex items-center justify-center rounded text-text-tertiary hover:text-accent"
        >
          <Check />
        </button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "group w-full px-3 py-2 rounded-md flex items-center gap-2",
        "transition-colors duration-fast ease-out cursor-pointer",
        active
          ? "bg-bg-inset text-text-primary"
          : "text-text-secondary hover:bg-bg-inset/60",
      )}
      onClick={onOpen}
    >
      <div className="flex-1 min-w-0">
        <div
          className="truncate text-small"
          onDoubleClick={(e) => {
            e.stopPropagation();
            startEditing();
          }}
        >
          {thread.title || "New chat"}
        </div>
        {showProjectBadge && (
          <div className="truncate text-caption text-text-tertiary mt-0.5">
            {thread.project_name ?? "All projects"}
          </div>
        )}
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          startEditing();
        }}
        aria-label="Rename thread"
        title="Rename"
        className={cn(
          "h-6 w-6 shrink-0 inline-flex items-center justify-center rounded",
          "text-text-tertiary opacity-0 group-hover:opacity-100",
          "hover:text-text-primary transition-opacity duration-fast",
        )}
      >
        <Pencil />
      </button>
      <span className="text-caption text-text-tertiary shrink-0">
        {relativeShort(thread.last_message_at)}
      </span>
    </div>
  );
}

function IconBtn({
  label,
  onClick,
  children,
  disabled,
  danger,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        "h-6 w-6 inline-flex items-center justify-center rounded-sm",
        "transition-colors duration-fast ease-out",
        disabled
          ? "text-text-quaternary cursor-not-allowed"
          : danger
            ? "text-text-tertiary hover:text-status-error hover:bg-bg-inset"
            : "text-text-tertiary hover:text-text-primary hover:bg-bg-inset",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
      )}
    >
      {children}
    </button>
  );
}
